/**
 * Detect drawing scale from PDF text content (title block / legend).
 * Used by staff and portal PDF viewers before manual calibration.
 */

export interface DetectedPdfScale {
  standardScaleId?: string;
  realPerPdfPoint: number;
  unitSystem: 'imperial' | 'metric';
  baseUnit: 'ft' | 'mm';
  displayUnit: 'ft-in' | 'm';
  label: string;
}

type PdfTextPage = {
  getTextContent: () => Promise<{ items: { str?: string }[] }>;
};

type PdfTextDocument = {
  getPage: (n: number) => Promise<PdfTextPage>;
};

/** Imperial architectural scales mapped to real feet per PDF point (72 pt = 1 inch on paper). */
const IMPERIAL_SCALE_PATTERNS: { re: RegExp; scaleId: string; paperInchesPerFoot: number }[] = [
  { re: /1\/4\s*["″]?\s*=\s*1['′\-]/i, scaleId: '1/4" = 1\'-0"', paperInchesPerFoot: 0.25 },
  { re: /1\/8\s*["″]?\s*=\s*1['′\-]/i, scaleId: '1/8" = 1\'-0"', paperInchesPerFoot: 0.125 },
  { re: /1\/2\s*["″]?\s*=\s*1['′\-]/i, scaleId: '1/2" = 1\'-0"', paperInchesPerFoot: 0.5 },
  { re: /3\/4\s*["″]?\s*=\s*1['′\-]/i, scaleId: '3/4" = 1\'-0"', paperInchesPerFoot: 0.75 },
  { re: /1\s*["″]?\s*=\s*1['′\-]/i, scaleId: '1" = 1\'-0"', paperInchesPerFoot: 1 },
  { re: /1\s*["″]?\s*=\s*10['′]/i, scaleId: '1" = 10\'-0"', paperInchesPerFoot: 0.1 },
  { re: /1\s*["″]?\s*=\s*20['′]/i, scaleId: '1" = 20\'-0"', paperInchesPerFoot: 0.05 },
  { re: /1\s*["″]?\s*=\s*30['′]/i, scaleId: '1" = 30\'-0"', paperInchesPerFoot: 1 / 30 },
  { re: /1\s*["″]?\s*=\s*40['′]/i, scaleId: '1" = 40\'-0"', paperInchesPerFoot: 0.025 },
  { re: /1\s*["″]?\s*=\s*50['′]/i, scaleId: '1" = 50\'-0"', paperInchesPerFoot: 0.02 },
  { re: /1\s*["″]?\s*=\s*60['′]/i, scaleId: '1" = 60\'-0"', paperInchesPerFoot: 1 / 60 },
];

const METRIC_RATIO_RE = /1\s*:\s*(\d+)/i;

export function parseScaleFromText(text: string): DetectedPdfScale | null {
  const normalized = text.replace(/\s+/g, ' ');

  for (const { re, scaleId, paperInchesPerFoot } of IMPERIAL_SCALE_PATTERNS) {
    if (re.test(normalized)) {
      // 1 PDF point = 1/72 inch on paper; real feet per point = (paperInchesPerFoot / 72) * realFeetPerPaperInch(1)
      const realFeetPerPaperInch = 1 / paperInchesPerFoot;
      const realPerPdfPoint = realFeetPerPaperInch / 72;
      return {
        standardScaleId: scaleId,
        realPerPdfPoint,
        unitSystem: 'imperial',
        baseUnit: 'ft',
        displayUnit: 'ft-in',
        label: scaleId,
      };
    }
  }

  const metricMatch = normalized.match(METRIC_RATIO_RE);
  if (metricMatch) {
    const ratio = Number(metricMatch[1]);
    if (ratio > 0 && ratio <= 1000) {
      // 1:100 => 1 mm on paper = 100 mm real => real mm per PDF point
      const realMmPerPdfPoint = ratio / (72 / 25.4);
      return {
        realPerPdfPoint: realMmPerPdfPoint,
        unitSystem: 'metric',
        baseUnit: 'mm',
        displayUnit: 'm',
        label: `1:${ratio}`,
      };
    }
  }

  return null;
}

export async function extractPdfPageText(pdfDoc: PdfTextDocument, pageNumber = 1): Promise<string> {
  try {
    const page = await pdfDoc.getPage(pageNumber);
    const content = await page.getTextContent();
    return content.items.map(item => item.str || '').join(' ');
  } catch {
    return '';
  }
}

export async function detectPdfScaleFromDocument(
  pdfDoc: PdfTextDocument,
  maxPages = 2,
): Promise<DetectedPdfScale | null> {
  for (let p = 1; p <= maxPages; p++) {
    const text = await extractPdfPageText(pdfDoc, p);
    const found = parseScaleFromText(text);
    if (found) return found;
  }
  return null;
}
